import { useState, useEffect } from 'react';
import { ethers } from 'ethers6';
import { executeWithRpcFailover } from '../utils/rpcProvider';
import { getRelayTokenPrice } from './useRelayPrice';
import { supabase } from '../utils/supabase';
import { fetchTokenBalancesFromExplorer } from '../utils/explorerApi';

// MavrkTokenLocker contract address
const LOCKER_ADDRESS = '0xd2C5947A6777500D9b7ad55a4D48AeF855AE6aBA';

// ERC20 ABI for getting token decimals if not provided by explorer
const ERC20_ABI = [
  "function decimals() view returns (uint8)",
  "function symbol() view returns (string)",
  "function name() view returns (string)"
];

export interface TokenLockData {
  tokenAddress: string;
  tokenAmount: number; // Human-readable amount
  symbol: string;
  name: string;
  logo_url?: string;
  tokenPriceUSD: number;
  usdValue: number;
  lockCount: number; // Number of individual locks for this token
}

export interface UseTokenLocksResult {
  locks: TokenLockData[];
  totalTVL: number;
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook to fetch and process all token locks from MavrkTokenLocker
 * Queries Ink Explorer API directly for token balances (much more efficient!)
 */
export function useTokenLocks(): UseTokenLocksResult {
  const [locks, setLocks] = useState<TokenLockData[]>([]);
  const [totalTVL, setTotalTVL] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchTokenLocks() {
      try {
        setIsLoading(true);
        setError(null);

        console.log('[TokenLocks] Fetching token balances from Ink Explorer API...');

        if (!isMounted) return;

        // Step 1: Fetch token balances directly from explorer API
        const explorerTokens = await fetchTokenBalancesFromExplorer(LOCKER_ADDRESS);
        
        console.log(`[TokenLocks] Found ${explorerTokens.length} tokens with balances from explorer API`);

        if (!isMounted) return;

        // Step 2: Get token metadata and prices
        const processedLocks: TokenLockData[] = [];
        
        if (explorerTokens.length > 0) {
          // First, try to get logo_urls from Supabase for known tokens
          const tokenAddresses = explorerTokens.map(t => t.tokenAddress?.toLowerCase()).filter(Boolean) as string[];
          console.log(`[TokenLocks] Querying Supabase for logos for ${tokenAddresses.length} token addresses:`, tokenAddresses);
          
          // Use case-insensitive matching by using OR conditions with ilike (exact match)
          const orConditions = tokenAddresses.map(addr => `contract_address.ilike.${addr}`).join(',');
          const { data: supabaseTokens, error: supabaseError } = await supabase
            .from('tokens')
            .select('contract_address, logo_url, name, symbol')
            .eq('network', 'ink')
            .or(orConditions);
          
          if (supabaseError) {
            console.warn('[TokenLocks] Supabase query error:', supabaseError);
          } else {
            console.log(`[TokenLocks] Found ${supabaseTokens?.length || 0} tokens in Supabase with logos`);
          }
          
          // Create a case-insensitive map
          const supabaseTokenMap = new Map(
            (supabaseTokens || []).map(t => [t.contract_address.toLowerCase(), t])
          );
          
          // Log which tokens have logos
          explorerTokens.forEach(t => {
            const addr = t.tokenAddress?.toLowerCase();
            const supabaseToken = supabaseTokenMap.get(addr || '');
            if (supabaseToken) {
              console.log(`[TokenLocks] Found logo for ${addr}: ${supabaseToken.logo_url}`);
            } else {
              console.warn(`[TokenLocks] No logo found in Supabase for ${addr}`);
            }
          });

          // Process each token from explorer
          const pricePromises = explorerTokens.map(async (explorerToken) => {
            try {
              const tokenAddress = explorerToken.tokenAddress!;
              const balanceBigInt = BigInt(explorerToken.balance || '0');
              
              // Get decimals - use from explorer if available, otherwise fetch from RPC
              let tokenDecimals: number;
              if (explorerToken.decimals !== undefined && explorerToken.decimals !== null) {
                // Convert to number if it's a string, or use directly if it's already a number
                tokenDecimals = typeof explorerToken.decimals === 'string' 
                  ? parseInt(explorerToken.decimals, 10) 
                  : Number(explorerToken.decimals);
                
                // Validate it's a valid number (between 0 and 255 for ERC20)
                if (isNaN(tokenDecimals) || tokenDecimals < 0 || tokenDecimals > 255) {
                  tokenDecimals = await executeWithRpcFailover(async (provider) => {
                    const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
                    const decimalsBigInt = await tokenContract.decimals();
                    return Number(decimalsBigInt);
                  });
                }
              } else {
                // No decimals from explorer, fetch from RPC
                tokenDecimals = await executeWithRpcFailover(async (provider) => {
                  const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
                  const decimalsBigInt = await tokenContract.decimals();
                  return Number(decimalsBigInt);
                });
              }

              // Get token metadata - prefer Supabase, then explorer, then defaults
              const supabaseToken = supabaseTokenMap.get(tokenAddress.toLowerCase());
              const symbol = explorerToken.symbol || supabaseToken?.symbol || '???';
              const name = explorerToken.name || supabaseToken?.name || 'Unknown Token';
              const logo_url = supabaseToken?.logo_url || explorerToken.logoUri || undefined;

              // Get token price from Relay Protocol API
              const tokenPriceUSD = await getRelayTokenPrice(tokenAddress);

              if (!tokenPriceUSD || tokenPriceUSD <= 0) {
                console.warn(`[TokenLocks] No price available for ${symbol} (${tokenAddress})`);
                return null;
              }

              // Format token amount using token decimals
              const tokenAmountHuman = Number(ethers.formatUnits(balanceBigInt, tokenDecimals));
              const usdValue = tokenAmountHuman * tokenPriceUSD;

              console.log(`[TokenLocks] ${symbol}: ${tokenAmountHuman.toFixed(2)} tokens @ $${tokenPriceUSD.toFixed(6)} = $${usdValue.toFixed(2)}`);

              return {
                tokenAddress,
                tokenAmount: tokenAmountHuman,
                symbol,
                name,
                logo_url,
                tokenPriceUSD,
                usdValue,
                lockCount: 1 // We don't track individual lock count with this approach
              };
            } catch (err) {
              console.warn(`[TokenLocks] Failed to process token ${explorerToken.tokenAddress}:`, err);
              return null;
            }
          });

          // Wait for all price calculations to complete
          const results = await Promise.all(pricePromises);
          const validResults = results.filter((result): result is NonNullable<typeof result> => result !== null);
          processedLocks.push(...validResults);
        }

        if (!isMounted) return;

        // Sort by USD value (highest first)
        processedLocks.sort((a, b) => b.usdValue - a.usdValue);

        // Calculate total TVL
        const tvl = processedLocks.reduce((sum, lock) => sum + lock.usdValue, 0);

        setLocks(processedLocks);
        setTotalTVL(tvl);
        setIsLoading(false);

        console.log('[TokenLocks] Total TVL:', tvl.toFixed(2), 'USD');
      } catch (err) {
        console.error('[TokenLocks] Error fetching locks:', err);
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setIsLoading(false);
        }
      }
    }

    fetchTokenLocks();

    return () => {
      isMounted = false;
    };
  }, []);

  return { locks, totalTVL, isLoading, error };
}
